import { Component, HostListener, inject, Input, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
	faAngleDown
    , faArrowLeft
    , faArrowRight
    , faUser
    , faUsers    
    , faGear
    , faQuestionCircle
    , faPersonCircleCheck
    , faCalendarCheck
    , faBuilding
    , faHouse
} from '@fortawesome/pro-solid-svg-icons';
import { HttpClient } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import { GlobalService } from '../../../shared/services/global.service';
import { pcgSettings } from '../../../shared/generated/pcg-settings';
import { SecurityService } from '../../../core/security/security.service';
import { NavigationService } from '../navigation.service';
import { UserJsVm } from '../../../shared/generated/Admin/Models/User/JavaScript/UserJsVm';
import { GlobalVariablesService } from '../../../services/global-variables.service';
import { Permission } from '../../../core/security/generated/Permission';
import { getMainNavRoutes } from '../../../core/main-nav-routes';
import { NotificationService } from '../../../services/notification.service';

@UntilDestroy()
@Component({
    selector: 'pcg-mat-side-nav'
    , templateUrl: './mat-side-nav.component.html'
    , styleUrls: ['./mat-side-nav.component.scss']
    , standalone: false
})
export class MatSideNavComponent implements OnInit {

    navService = inject(NavigationService);
    public router = inject(Router);
    public globalService = inject(GlobalService);
    public gvs = inject(GlobalVariablesService);
    private sec = inject(SecurityService);
    private httpClient = inject(HttpClient);
    private ns = inject(NotificationService);

	@Input() user!: UserJsVm;

    navContentOffscreen: boolean = false;
    // Setting the scroll height used to keep the nested table scrollable with headers shown.
	observer = new IntersectionObserver(entries => { this.navContentOffscreen = !entries[0]?.isIntersecting; });
	// Used to display a loading spinner on the navigation link when clicked, notifying the user that the page is loading.
	navLinkLoading = signal<[string, number]>([null!, null!]);

    mode = pcgSettings.mode;
    hoverText: string = "";
    fileLogoUrl = "../assets/images/logos/logo_small.png";
    navName = "";

    navigationOffset = 0;

	security: Permission = localStorage.dynamicSecurity 
        ? JSON.parse(localStorage.dynamicSecurity) 
        : null;
	
	// Fa icons
	faAngleDown = faAngleDown;
    faUser = faUser;
    faGear = faGear;
    faQuestionCircle = faQuestionCircle;
    faUsers = faUsers;
    faArrowLeft = faArrowLeft;
    faArrowRight = faArrowRight;
    faPersonCircleCheck = faPersonCircleCheck
    faCalendarCheck = faCalendarCheck;
    faBuilding = faBuilding;
    faHouse = faHouse;

	xDown = null;
	yDown = null;

    @HostListener('window:resize') onResize() { this.fixNavOffset(); }

	ngOnInit() {
        this.navService.navRoutes.set(this.sec.getSecureNavItems(getMainNavRoutes()));
		this.navService.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
		this.navService.navRoutes$
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.fixNavOffset();
				this.navLinkLoading.set([null!, null!]);
			}
		);

        //event litseners for swiping away side nav in mobile
		document.addEventListener('touchstart', (evt) => this.handleTouchStart(evt), false);        
		document.addEventListener('touchmove', (evt) => this.handleTouchMove(evt), false);

		// Checking if there is any nave content off-screen.
		const navAnchor = document.getElementById("navAnchor");
		this.observer?.observe(navAnchor!);
	}

    fixNavOffset() {
		const mainNav = document.getElementById("pcgMainNavigation");
		setTimeout(() => {
			if (getComputedStyle(mainNav!, null).position === "fixed") { this.navigationOffset = mainNav?.getBoundingClientRect()?.bottom!; } 
			else { this.navigationOffset = 0; }

			const styles = `pcg-side-nav { top: ${this.navigationOffset - 1}px }` 
				+ `.nav-padding-placeholder { padding-top: ${this.navigationOffset}px; }` 
                + `.conversations-sidenav { top: ${this.navigationOffset}px }` 
				+ `.knowledge-base-doc-popout { top: ${this.navigationOffset}px }` 
				+ `.popout { top: ${this.navigationOffset}px }`;

			this.setDynamicStyle("navPaddingOffset", styles);
		}, 100);
	}

	setDynamicStyle(
		name: string
		, styles: string
	) {
		let dynamicStyle = document.getElementById(name);
		if (dynamicStyle) { dynamicStyle.remove(); }
		dynamicStyle = document.createElement("style");
		dynamicStyle.id = name;
		dynamicStyle.innerHTML = styles;
		document.head.appendChild(dynamicStyle);
	}

	redirect(path: string) { this.router.navigateByUrl(path); }

	openInNewTab(namedRoute: string) {
		let newRelativeUrl = this.router.createUrlTree([namedRoute]);
		let baseUrl = globalThis.location.href.replace(this.router.url, '');
		window.open(baseUrl + newRelativeUrl, '_blank');
		this.navService.isNavOpen.set(false);
	}

	toggleUserProfileSubNav() {
		const isOpen = this.navService.userProfileExpanded();
		this.navService.collapseAllSubNavs();
		if (!isOpen) { this.navService.userProfileExpanded.set(true); }
	}

	handleTouchStart(evt: any) {
		if (
			window.innerWidth < 993 
			&& this.navService.isNavOpen()
		) {
			let firstTouch = evt.touches || evt.originalEvent.touches;       
			this.xDown = firstTouch[0].clientX;                                      
			this.yDown = firstTouch[0].clientY; 
		}			                                   
	};                                                
																				
	handleTouchMove(evt: any) {
		if (
			this.navService.isMobile() 
			&& this.navService.isNavOpen()
		) {
			if (
				!this.xDown 
				|| !this.yDown
			) { return; }
	
			let xUp = evt.touches[0].clientX;                                    
			let yUp = evt.touches[0].clientY;
			let xDiff = this.xDown - xUp;
			let yDiff = this.yDown - yUp;
																				
			if (Math.abs(xDiff) > Math.abs(yDiff)) {/*most significant*/
				if (xDiff > 8) {
					/* right swipe */ 
					this.navService.isNavOpen.set(false);
				} else { /* left swipe */ }                       
			} 
			/* reset values */
			this.xDown = null;
			this.yDown = null;  
		}
	}

    navLinkClicked(
		name: any
		, level: any
        , openNewTab = false
        , path = ""
	) {
        if (openNewTab) { window.open(path); }
        else {
            if (this.navService.isMobile()) { this.navService.isNavOpen.set(false); }
            this.navLinkLoading.set([name, level]);
            setTimeout(() => { this.navLinkLoading.set([null!, null!]); }, 500);
        }
        
	}

    navLinkIsLoading(
		name: any
		, level: any
	) {
		const [currentName, currentLevel] = this.navLinkLoading();
		return (currentName === name && currentLevel === level);
	}

    scrollToBottom() {
		const navAnchor = document.getElementById('navAnchor');
		if (navAnchor) { navAnchor.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' }); }
	}

    // #region logout
    logout() {
		this.gvs.isNavFixed.set(false);
		this.navService.collapseAllSubNavs();
		this.sec.setSecurity(null!, null!, null!);
		localStorage.removeItem("subNavPosition");
		sessionStorage.clear();
		this.deleteAllCookies();

        this.httpClient.get(`api/Account/Logout?userId=${this.user.id}`).subscribe((msg: any) => {
			if (msg.value != null) { globalThis.location.href = msg.value; }
			if (!msg.isSuccessful) { this.ns.showError(msg.message, {timeOut: 5000}); }
		});
		this.router.navigate(['/login']);
	}

    deleteAllCookies() {
		const cookies = document.cookie.split(';');
		for (const element of cookies) {
			const cookie = element;
			const eqPos = cookie.indexOf('=');
			const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
			document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
		}
	}
    // #endregion
}
